import {
  Minter_Type_Names_Enum,
  ProjectDetailFragment,
  Projects_Metadata_Bool_Exp,
  ProjectStatusFragment,
  ProjectVerticalDetailFragment
} from '@/gql/generated/graphql'
import { Platform } from '@/mongo/services/activity/types'
import { MARFA_RESIDENCY_PROJECT_IDS, OPENSEA_SEADROP_COLLECTION_URLS } from '@consts/common'

export enum ValidStatusType {
  live = 'LIVE',
  upcoming = 'UPCOMING',
  complete = 'COMPLETE',
  paused = 'PAUSED'
}

export const ALL_VERTICALS = [
  'curated',
  'studio',
  'engine',
  'presents',
  'collaborations',
  'explorations',
  'factory',
  'playground'
] as const
export type Vertical = (typeof ALL_VERTICALS)[number]

export function getVerticalFilter(vertical: string): Projects_Metadata_Bool_Exp {
  if (!vertical) {
    return {}
  }

  if (vertical === 'ab-500') {
    vertical = 'ab500'
  }

  if (vertical.includes('curated series') || vertical.includes('ab500')) {
    return { tags: { tag_name: { _eq: vertical } } }
  }

  if (vertical === 'engine') {
    return { vertical_name: { _in: ['flex', 'fullyonchain'] } }
  }

  if (vertical === 'collaborations') {
    return { vertical: { category_name: { _eq: vertical } } }
  }

  return { vertical_name: { _eq: vertical } }
}

export function isEngine(project: ProjectVerticalDetailFragment): boolean {
  return project.vertical_name === 'flex' || project.vertical_name === 'fullyonchain'
}

export function getVertical(project: ProjectVerticalDetailFragment): Vertical {
  if (isEngine(project)) {
    return 'engine'
  }
  if (project.vertical.category_name === 'collaborations') {
    return 'collaborations'
  }
  return project.vertical_name as Vertical
}

export function getVerticalTitle(project: ProjectVerticalDetailFragment): string {
  if (isEngine(project)) {
    if (project.contract.name?.toLowerCase().includes('moment')) {
      return 'Bright Moments'
    }
    return project.contract.name ?? 'Art Blocks Engine'
  }
  if (project.vertical?.category_name === 'collaborations') {
    return project.vertical.display_name
  }
  return (
    'Art Blocks ' + project.vertical?.display_name?.charAt(0).toUpperCase() + project.vertical?.display_name?.slice(1)
  )
}

export function getVerticalTitleFromMongoPlatform(platform: Platform): string {
  switch (platform) {
    case Platform.ArtBlocksCurated:
      return 'Art Blocks Curated'
    case Platform.ArtBlocksPresents:
    case Platform.ArtBlocksFactory:
    case Platform.ArtBlocksPlayground:
      return 'Art Blocks Presents'
    case Platform.ArtBlocksCollaborations:
      return 'Art Blocks Collaborations'
    case Platform.ArtBlocksExplorations:
      return 'Art Blocks Explorations'
    case Platform.ArtBlocksStudio:
      return 'Art Blocks Studio'
    default:
      return 'Art Blocks Engine'
  }
}

const RAM_MINTER_TYPES: Array<Minter_Type_Names_Enum | undefined> = [Minter_Type_Names_Enum.MinterRamv0]

export function isRAMMinter(minterType: Minter_Type_Names_Enum | undefined): boolean {
  return RAM_MINTER_TYPES.includes(minterType)
}

export const getProjectStatus = (project: ProjectStatusFragment | undefined): ValidStatusType => {
  const startTime = project?.auction_start_time ?? project?.start_datetime

  const auctionUpcoming = startTime && new Date(startTime).getTime() > Date.now() && !project?.paused
  const fixedPriceUpcoming = project?.paused && startTime && new Date(startTime).getTime() > Date.now()

  const ramComplete =
    isRAMMinter(project?.minter_configuration?.minter?.minter_type) &&
    (project?.bids_aggregate?.aggregate?.count ?? 0) >=
      (project?.minter_configuration?.extra_minter_details.numTokensInAuction ?? 1) &&
    project?.auction_end_time &&
    new Date(project.auction_end_time) < new Date()

  if (
    project?.completed_at !== null ||
    project?.minter_configuration?.max_invocations === project?.invocations ||
    ramComplete
  ) {
    return ValidStatusType.complete
  } else if (auctionUpcoming || fixedPriceUpcoming) {
    return ValidStatusType.upcoming
  } else if (project?.paused || !startTime) {
    return ValidStatusType.paused
  }
  return ValidStatusType.live
}

const projectIdRegex = /^0x[a-fA-F0-9]{40}-\d+$/

export const isProjectId = (idOrSlug: string): boolean => {
  return projectIdRegex.test(idOrSlug)
}

/**
 * Builds a GraphQL where clause for querying project metadata based on an ID or slug.
 *
 * This function constructs a where clause for use in a GraphQL query to fetch project metadata.
 * It handles two types of inputs:
 * 1. A project ID (e.g., "0x...") - In this case, it creates a clause to match the exact ID.
 * 2. A project slug (e.g., "project-name-by-artist") - In this case, it creates a clause to match the project name.
 *
 * In both cases, it includes additional conditions to ensure only curated and collaborative projects are returned.
 *
 * @param {string} idOrSlug - The project ID or slug to query for.
 * @returns {Projects_Metadata_Bool_Exp} A GraphQL where clause object for querying project metadata.
 */
export function buildProjectWhereClause(idOrSlug: string): Projects_Metadata_Bool_Exp {
  let whereClause: Projects_Metadata_Bool_Exp

  if (isProjectId(idOrSlug)) {
    whereClause = {
      id: {
        _eq: idOrSlug.toLowerCase()
      }
    }
  } else {
    whereClause = {
      slug: {
        _eq: idOrSlug
      }
    }
  }
  return whereClause
}

/**
 * Is the project an Art Blocks x OpenSea Marfa residency project
 *
 * @param projectDbId - The database ID of the project (ie. 0x0000000000000000000000000000000000000000-0)
 */
export function isMarfaResidency(projectDbId: string): boolean {
  return MARFA_RESIDENCY_PROJECT_IDS.includes(projectDbId)
}

/**
 * Is the project an OpenSea SeaDrop minting project
 *
 * @param projectDbId - The database ID of the project (ie. 0x0000000000000000000000000000000000000000-0)
 */
export function isOpenSeaSeaDropProject(projectDbId: string): boolean {
  return !!getOpenSeaSeaDropProjectUrl(projectDbId)
}

/**
 * Get the OpenSea SeaDrop minting collection url for a project
 *
 * @param projectDbId - The database ID of the project (ie. 0x0000000000000000000000000000000000000000-0)
 */
export function getOpenSeaSeaDropProjectUrl(projectDbId: string): string | null {
  return OPENSEA_SEADROP_COLLECTION_URLS[projectDbId] ?? null
}

export function isAb500(project: ProjectDetailFragment): boolean {
  return project.tags?.some((tag) => tag.tag?.name === 'ab500') ?? false
}
